import React, { useId } from 'react';
import { bool, func, node, string } from 'prop-types';
import classNames from 'classnames';

import css from './Toggle.module.css';

/**
 * An on/off switch from the Components board, in the family colour. With a `label` (and an optional
 * `description`) it is the board's row, "Episode reminders · An email 1 hour before", with the
 * switch on the right; the label names the switch and the description describes it. Without a
 * label, give it an `ariaLabel`.
 */
const Toggle = props => {
  const {
    className,
    rootClassName,
    checked,
    onChange,
    disabled,
    ariaLabel,
    name,
    label,
    description,
  } = props;
  const id = useId();

  const handleClick = () => {
    if (disabled || !onChange) return;
    onChange(!checked);
  };

  const descriptionId = description ? `${id}-description` : undefined;
  const switchButton = (
    <button
      id={id}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label ? undefined : ariaLabel}
      aria-describedby={descriptionId}
      name={name}
      disabled={disabled}
      onClick={handleClick}
      className={classNames(css.switch, { [css.checked]: checked })}
    >
      <span className={css.thumb} />
    </button>
  );

  if (!label) {
    return <span className={classNames(rootClassName || css.root, className)}>{switchButton}</span>;
  }

  return (
    <div
      className={classNames(rootClassName || css.row, className, { [css.rowDisabled]: disabled })}
    >
      <span className={css.text}>
        <label htmlFor={id} className={css.label}>
          {label}
        </label>
        {description ? (
          <span id={descriptionId} className={css.description}>
            {description}
          </span>
        ) : null}
      </span>
      {switchButton}
    </div>
  );
};

Toggle.defaultProps = {
  className: null,
  rootClassName: null,
  checked: false,
  onChange: null,
  disabled: false,
  ariaLabel: null,
  name: null,
  label: null,
  description: null,
};

Toggle.propTypes = {
  className: string,
  rootClassName: string,
  checked: bool,
  onChange: func,
  disabled: bool,
  // Names the switch when there is no `label`
  ariaLabel: string,
  name: string,
  // The row: a label on the left (it names the switch) and a line under it
  label: node,
  description: node,
};

export default Toggle;
